import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AppDrawerGutterComponent } from '@portal-client/app/components/app-drawer-gutter/app-drawer-gutter.component';
import { AppDrawerNavComponent } from '@portal-client/app/components/app-drawer-nav/app-drawer-nav.component';
import { AppGutterComponent } from '@portal-client/app/components/app-gutter/app-gutter.component';
import { AppNavComponent } from '@portal-client/app/components/app-nav/app-nav.component';
import { AppNavbarUserLaunchPadComponent } from '@portal-client/app/components/app-navbar-user-launch-pad/app-navbar-user-launch-pad.component';
import { DomService } from '@portal-core/general/services/dom.service';
import { PageChromeService } from '@portal-core/layout/services/page-chrome.service';
import { LayoutService } from '@portal-core/layout/services/layout.service';
import { StatusIncidentBarStateService } from '@portal-core/status-incidents/services/status-incident-bar-state.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { Breakpoints } from '@portal-core/util/breakpoints';
import { Observable, Subscription, filter, map } from 'rxjs';

// These px values are hand-kept in sync with _navbar-variables.scss and _toolbar-variables.scss and nothing
// enforces that today (the sass-to-json build step only exports color themes, not layout dimensions) - if either
// SCSS variable changes, update these to match.
// Matches $navbar-height (5.4rem) in _navbar-variables.scss, converted to px (root font-size is 62.5% -> 1rem = 10px)
const BASE_TOP_GAP = 54;
// Matches $toolbar-height-normal (4rem) in _toolbar-variables.scss, converted to px
const MAINTENANCE_BAR_HEIGHT = 40;

/**
 * mc-app-shell
 * The persistent authenticated shell for the portal-client app (the single-outlet successor to the
 * SubPageFillComponent + SubNavbarComponent named-outlet layout). It owns the toolbar, drawer, left
 * app-nav, and user launch-pad — all mounted once and kept across navigation — plus a single primary
 * <router-outlet> for the routed page. Each page's heading/navbar toolbar content is projected in
 * via PageChromeService (see mc-page), so only those slots swap on navigation.
 */
@Component({
  selector: 'mc-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatSidenavContainer, MatSidenav, MatSidenavContent, MatToolbar, MatButton, MatIcon,
    NgIf, AsyncPipe, NgTemplateOutlet, RouterOutlet,
    AppNavComponent, AppNavbarUserLaunchPadComponent, AppDrawerNavComponent, AppDrawerGutterComponent, AppGutterComponent
  ]
})
@AutoUnsubscribe()
export class AppShellComponent implements OnInit {
  @ViewChild('drawer', { static: true }) drawer: MatSidenav;
  @ViewChild('drawer', { read: ElementRef, static: true }) drawerElementRef: ElementRef;

  readonly pageChrome = inject(PageChromeService);

  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly domService = inject(DomService);
  private readonly layoutService = inject(LayoutService);
  private readonly statusIncidentBarStateService = inject(StatusIncidentBarStateService);

  showAppNav: Observable<BreakpointState>;
  showAppDrawer: Observable<BreakpointState>;
  showAppDrawerNav: Observable<BreakpointState>;
  showAppDrawerGutter: Observable<BreakpointState>;
  showAppNavbarLaunchPad: Observable<BreakpointState>;
  showPageGutter: Observable<BreakpointState>;

  /**
   * The drawer's top offset. The drawer is fixed to the viewport, so it has to clear the status
   * incident bar above the toolbar whenever that bar is showing.
   */
  topGap$: Observable<number> = this.statusIncidentBarStateService.visible$.pipe(
    map(visible => visible ? BASE_TOP_GAP + MAINTENANCE_BAR_HEIGHT : BASE_TOP_GAP)
  );

  routerEventSubscription: Subscription;
  showAppDrawerSubscription: Subscription;

  constructor() {
    this.showAppNav = this.breakpointObserver.observe(Breakpoints.appNavBreakpoint);
    this.showAppDrawer = this.breakpointObserver.observe(Breakpoints.appDrawerBreakpoint);
    this.showAppDrawerNav = this.breakpointObserver.observe(Breakpoints.appDrawerNavBreakpoint);
    this.showAppDrawerGutter = this.breakpointObserver.observe(Breakpoints.appDrawerGutterBreakpoint);
    this.showAppNavbarLaunchPad = this.breakpointObserver.observe(Breakpoints.appNavbarLaunchPadBreakpoint);
    this.showPageGutter = this.breakpointObserver.observe(Breakpoints.pageGutterBreakpoint);
  }

  ngOnInit() {
    // Close the drawer on navigation.
    this.routerEventSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => this.drawer.close());

    // Close the drawer when the app-drawer breakpoint no longer applies.
    this.showAppDrawerSubscription = this.showAppDrawer.subscribe(showAppDrawer => {
      if (!showAppDrawer.matches) {
        this.drawer.close();
      }
    });
  }

  onLogoClicked() {
    this.layoutService.emitOpenAboutDialog();
  }

  onDrawerClicked(event: MouseEvent) {
    if (event.target) {
      if (this.domService.withinClass(event.target as Element, 'mc-js-close-drawer', this.drawerElementRef.nativeElement)) {
        this.drawer.close();
      }
    }
  }
}
